import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import {
  createSession,
  getLazyFile,
  getSession,
  getTree,
  LazyFileRow,
  SessionResponse,
  triggerLazyDownload,
  TreeNode,
} from "../lib/api";
import { AskClaudeRequest, AskTarget } from "../lib/askMessage";
import ErrorBox from "../components/ErrorBox";
import FileTree from "../components/FileTree";
import FileViewerHost from "../components/FileViewerHost";
import SummaryPane from "../components/SummaryPane";
import PreprintPane from "../components/PreprintPane";
import ChatBox from "../components/ChatBox";

/**
 * Reviewer screen — three resizable panes.
 *
 * Layout:
 *   ┌──────────────┬────────────────────────────────┐
 *   │ FileTree     │ FileViewer | Summary (tab)     │
 *   │              ├────────────────────────────────┤
 *   │              │ Chat box (stub in M1, real M2) │
 *   └──────────────┴────────────────────────────────┘
 *
 * On mount: POST /api/sessions to create + ingest, then GET /api/sessions/{sid}/tree.
 * On file click: child component handles fetching content + rendering.
 */
export default function Reviewer() {
  const { egusphereId } = useParams<{ egusphereId: string }>();
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [tree, setTree] = useState<TreeNode | null>(null);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Active lazy-download polling. Path → most-recent LazyFileRow. */
  const [lazyDownloads, setLazyDownloads] = useState<
    Record<string, LazyFileRow>
  >({});
  const lazyPollersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map(),
  );
  /** Select-to-ask (issue #56): latest "Ask Claude" click from the viewer.
   *  The nonce lets ChatBox tell a repeat ask on the same lines apart from
   *  a re-render of the same request. It must stay monotonic for the whole
   *  Reviewer lifetime (a ref, NOT derived from the previous request):
   *  askRequest resets to null on paper navigation, and a restarting nonce
   *  could collide with the last consumed value and wrongly suppress the
   *  first ask on the next paper. */
  const [askRequest, setAskRequest] = useState<AskClaudeRequest | null>(null);
  const askNonceRef = useRef(0);
  const onAskClaude = useCallback((req: AskTarget) => {
    askNonceRef.current += 1;
    setAskRequest({ ...req, nonce: askNonceRef.current });
  }, []);

  // Create the session + load the tree. Re-runs if the user navigates to a
  // different paper without unmounting the component.
  useEffect(() => {
    if (!egusphereId) return;
    let cancelled = false;
    setSession(null);
    setTree(null);
    setSelectedPath(null);
    setError(null);
    // A pending ask from the previous paper must not survive navigation —
    // a remounted ChatBox (fresh nonce ref) would re-fire it into the new
    // paper's chat.
    setAskRequest(null);
    (async () => {
      try {
        const s = await createSession(egusphereId);
        if (cancelled) return;
        setSession(s);
        if (s.status === "ready") {
          const t = await getTree(s.id);
          if (!cancelled) setTree(t);
        } else if (s.status === "failed") {
          setError(s.error ?? "Ingest failed");
        } else {
          // Poll until ready (synchronous M1 ingest means this rarely fires,
          // but we keep the loop for when M3 makes ingest async).
          await pollUntilReady(s.id, async (latest) => {
            if (cancelled) return;
            setSession(latest);
          });
          if (!cancelled) {
            const t = await getTree(s.id);
            setTree(t);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [egusphereId]);

  const onLazyClick = useCallback(
    async (node: TreeNode) => {
      if (!session || node.lazyFileId == null) return;
      const sid = session.id;
      const lfid = node.lazyFileId;
      const path = node.id;
      try {
        const row = await triggerLazyDownload(sid, lfid);
        setLazyDownloads((m) => ({ ...m, [path]: row }));
        // Start polling if there isn't already one in flight.
        if (!lazyPollersRef.current.has(path)) {
          const handle = setInterval(async () => {
            try {
              const r = await getLazyFile(sid, lfid);
              setLazyDownloads((m) => ({ ...m, [path]: r }));
              if (r.status === "ready" || r.status === "failed") {
                clearInterval(handle);
                lazyPollersRef.current.delete(path);
                if (r.status === "ready") {
                  // Refresh the tree so the file becomes a normal node.
                  const t = await getTree(sid);
                  setTree(t);
                }
              }
            } catch {
              clearInterval(handle);
              lazyPollersRef.current.delete(path);
            }
          }, 2000);
          lazyPollersRef.current.set(path, handle);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [session],
  );

  // Stop any active pollers on unmount.
  useEffect(() => {
    return () => {
      for (const h of lazyPollersRef.current.values()) clearInterval(h);
      lazyPollersRef.current.clear();
    };
  }, []);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-lg">
          <ErrorBox title="Couldn't load this paper" detail={error} />
        </div>
      </div>
    );
  }
  if (!egusphereId) return null;

  return (
    <PanelGroup direction="horizontal" className="h-full">
      {/* LEFT: file tree */}
      <Panel defaultSize={22} minSize={15}>
        <div className="flex h-full flex-col border-r border-ink-200 bg-white">
          <div className="border-b border-ink-200 px-3 py-2 text-xs uppercase tracking-wide text-ink-500">
            {egusphereId}
            {session && (
              <span className="ml-2 text-ink-700">
                · {session.file_count} files
              </span>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {tree ? (
              <FileTree
                node={tree}
                onSelect={(path) => setSelectedPath(path)}
                selectedPath={selectedPath}
                onLazyClick={onLazyClick}
              />
            ) : (
              <div className="p-3 text-sm text-ink-500">
                {session?.status === "ingesting"
                  ? "Downloading and unpacking files…"
                  : "Preparing files…"}
              </div>
            )}
          </div>
          {Object.entries(lazyDownloads).length > 0 && (
            <div className="border-t border-ink-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
              <div className="mb-0.5 font-semibold">Downloads</div>
              {Object.entries(lazyDownloads).map(([path, row]) => (
                <LazyDownloadRow key={path} path={path} row={row} />
              ))}
            </div>
          )}
        </div>
      </Panel>

      <PanelResizeHandle className="w-px bg-ink-200 hover:bg-ink-300" />

      {/* RIGHT: viewer + summary on top, chat on bottom */}
      <Panel defaultSize={78}>
        <PanelGroup direction="vertical">
          <Panel defaultSize={40} minSize={30}>
            <RightTop
              sessionId={session?.id ?? null}
              egusphereId={egusphereId}
              selectedPath={selectedPath}
              onAskClaude={onAskClaude}
            />
          </Panel>
          <PanelResizeHandle className="h-px bg-ink-200 hover:bg-ink-300" />
          <Panel defaultSize={60} minSize={15}>
            <ChatBox sessionId={session?.id ?? null} askRequest={askRequest} />
          </Panel>
        </PanelGroup>
      </Panel>
    </PanelGroup>
  );
}

/** Tabbed FileViewer / Summary host. */
function RightTop(props: {
  sessionId: number | null;
  egusphereId: string;
  selectedPath: string | null;
  onAskClaude?: (req: AskTarget) => void;
}) {
  const [tab, setTab] = useState<"file" | "summary" | "preprint">("file");
  return (
    <div className="flex h-full flex-col">
      <div className="flex gap-1 border-b border-ink-200 bg-ink-100 px-2 pt-1">
        <Tab active={tab === "file"} onClick={() => setTab("file")}>
          Files
        </Tab>
        <Tab active={tab === "summary"} onClick={() => setTab("summary")}>
          Summary
        </Tab>
        <Tab active={tab === "preprint"} onClick={() => setTab("preprint")}>
          Preprint
        </Tab>
      </div>
      <div className="flex-1 overflow-hidden bg-white">
        {tab === "file" ? (
          <FileViewerHost
            sessionId={props.sessionId}
            path={props.selectedPath}
            onAskClaude={props.onAskClaude}
          />
        ) : tab === "summary" ? (
          <SummaryPane egusphereId={props.egusphereId} />
        ) : (
          <PreprintPane egusphereId={props.egusphereId} />
        )}
      </div>
    </div>
  );
}

function Tab(props: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={props.onClick}
      className={`rounded-t border border-b-0 px-3 py-1 text-xs ${
        props.active
          ? "border-ink-200 bg-white text-ink-900"
          : "border-transparent text-ink-500 hover:text-ink-700"
      }`}
    >
      {props.children}
    </button>
  );
}

function LazyDownloadRow(props: { path: string; row: LazyFileRow }) {
  const r = props.row;
  const pct =
    r.size_bytes > 0 ? Math.floor((r.progress_bytes / r.size_bytes) * 100) : 0;
  const label = (() => {
    if (r.status === "ready") return "✓ ready";
    if (r.status === "failed") return `✗ ${r.error?.slice(0, 60) ?? "failed"}`;
    if (r.status === "downloading") return `↓ ${pct}%`;
    return "queued";
  })();
  return (
    <div className="truncate">
      <span className="font-mono">{props.path}</span> — {label}
    </div>
  );
}

async function pollUntilReady(
  sid: number,
  onUpdate: (s: SessionResponse) => void,
): Promise<void> {
  // Synchronous M1 ingest means we usually return on the first iteration, but
  // a slow Zenodo can leave us in `ingesting` for several seconds. M3 will
  // switch this for SSE.
  while (true) {
    const s = await getSession(sid);
    onUpdate(s);
    if (s.status === "ready" || s.status === "failed") return;
    await new Promise((r) => setTimeout(r, 1500));
  }
}
